/**
 * shared/utils/property.js
 * ------------------------
 * Shared utilities for property objects. ONE place to handle:
 *  - image resolution across legacy + new schemas
 *  - thumbnail fallbacks
 *
 * v2.71.54 — Centralized image resolver to fix the bug where horizontal
 * PropertyCards rendered without images. Root cause: raw `images[0]`
 * (often a relative `/api/files/...` path) was being used without
 * prepending the backend URL — so the browser fetched it from the
 * frontend domain and got 404.
 */

import { resolveImage } from "../services/api";

/**
 * Safely resolve a thumbnail URL for any property shape we receive.
 *
 * Handles:
 *  - p.images = ["https://...", ...]                       (string array)
 *  - p.images = [{ url: "https://..." }, ...]              (object array)
 *  - p.images = ["/api/files/..."]                         (relative backend path)
 *  - p.featured_image / p.image / p.thumbnail              (single-field fallbacks)
 *  - p.cover_image_snapshot (shortlist items use this)
 *  - undefined / empty / null                              (returns "")
 *
 * Returns a fully-qualified URL ready to drop into <img src> or
 * background-image. Returns "" when no image is available — callers
 * should render an icon placeholder in that case.
 */
export function getPropertyThumbnail(p) {
  if (!p) return "";

  // 1) primary: images[] array (most common)
  if (Array.isArray(p.images) && p.images.length > 0) {
    const first = p.images[0];
    if (typeof first === "string" && first.trim()) {
      return resolveImage(first);
    }
    if (first && typeof first === "object") {
      const src = first.url || first.src || first.image || "";
      if (src) return resolveImage(src);
    }
  }

  // 2) fallback: single-string image fields
  const single =
    p.featured_image ||
    p.image ||
    p.thumbnail ||
    p.cover_image ||
    p.cover_image_snapshot ||
    "";
  if (single && typeof single === "string") {
    return resolveImage(single);
  }

  return "";
}

/**
 * Convenience builder for inline backgroundImage styles. Returns
 * `{ backgroundImage: 'url(...)' }` or `{}` when no image is available.
 */
export function thumbnailStyle(p) {
  const url = getPropertyThumbnail(p);
  return url ? { backgroundImage: `url(${url})` } : {};
}
